import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import SEO from "../components/SEO";
import axios from "axios";
import CandidatoAvatar from "../components/CandidatoAvatar";
import {
  ArrowLeft,
  Users,
  TrendingUp,
  TrendingDown,
  Building2,
  MapPin,
  DollarSign,
  BarChart3,
  Factory,
  Wheat,
  Landmark,
  Package,
  ChevronDown,
  ChevronUp,
  Stethoscope,
} from "lucide-react";

const formatBRL = (val) => {
  if (!val || val === 0) return "R$ 0";
  if (Math.abs(val) >= 1e9) return `R$ ${(val / 1e9).toFixed(2)} tri`;
  if (Math.abs(val) >= 1e6) return `R$ ${(val / 1e6).toFixed(2)} bi`;
  if (Math.abs(val) >= 1e3) return `R$ ${(val / 1e3).toFixed(2)} mi`;
  return `R$ ${val.toLocaleString("pt-BR", { maximumFractionDigits: 0 })} mil`;
};

const formatUSD = (val) => {
  if (!val || val === 0) return "US$ 0";
  if (Math.abs(val) >= 1e9) return `US$ ${(val / 1e9).toFixed(2)} bi`;
  if (Math.abs(val) >= 1e6) return `US$ ${(val / 1e6).toFixed(2)} mi`;
  return `US$ ${val.toLocaleString("pt-BR", { maximumFractionDigits: 0 })}`;
};

const formatPop = (val) => new Intl.NumberFormat("pt-BR").format(val || 0);

function KPICard({ label, value, icon, color = "blue", subtitle }) {
  const colorMap = {
    blue: "from-blue-500 to-blue-600",
    green: "from-green-500 to-green-600",
    purple: "from-purple-500 to-purple-600",
    orange: "from-orange-500 to-orange-600",
    red: "from-red-500 to-red-600",
    emerald: "from-emerald-500 to-emerald-600",
    cyan: "from-cyan-500 to-cyan-600",
    rose: "from-rose-500 to-rose-600",
  };
  return (
    <div
      className={`bg-gradient-to-br ${colorMap[color] || colorMap.blue} rounded-xl p-4 text-white shadow-lg`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wider text-white/80 mb-1">
            {label}
          </p>
          <p className="text-xl font-bold truncate">{value}</p>
          {subtitle && (
            <p className="text-xs text-white/70 mt-1">{subtitle}</p>
          )}
        </div>
        <div className="p-2 rounded-lg bg-white/20 ml-2">{icon}</div>
      </div>
    </div>
  );
}

function SectionCard({ title, subtitle, children, className = "" }) {
  return (
    <div
      className={`bg-[var(--card)] border border-[var(--border)] rounded-xl p-5 ${className}`}
    >
      {title && (
        <div className="mb-4">
          <h3 className="text-lg font-bold text-[var(--text)]">{title}</h3>
          {subtitle && (
            <p className="text-sm text-[var(--muted)]">{subtitle}</p>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

function ProductBar({ rank, name, value, maxValue, color = "#3b82f6" }) {
  const pct = maxValue > 0 ? (value / maxValue) * 100 : 0;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="text-xs font-mono text-[var(--muted)] w-5 text-right">
        {rank}.
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-sm text-[var(--text)] truncate">{name}</span>
          <span className="text-xs text-[var(--muted)] ml-2 whitespace-nowrap">
            {formatUSD(value)}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-[var(--border)] overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${Math.max(pct, 0.5)}%`,
              backgroundColor: color,
            }}
          />
        </div>
      </div>
    </div>
  );
}

function VABDonut({ vab }) {
  if (!vab || !vab.vabTotal || vab.vabTotal === 0) return null;

  const sectors = [
    { label: "Agropecuária", value: vab.vabAgro, color: "#22c55e", icon: "🌾" },
    { label: "Indústria", value: vab.vabIndustria, color: "#3b82f6", icon: "🏭" },
    { label: "Serviços", value: vab.vabServicos, color: "#f59e0b", icon: "🏢" },
    { label: "Adm. Pública", value: vab.vabAdmPublica, color: "#8b5cf6", icon: "🏛️" },
  ].filter((s) => s.value > 0);

  const total = sectors.reduce((s, x) => s + x.value, 0);

  return (
    <div className="space-y-2">
      {sectors.map((s, i) => {
        const pct = total > 0 ? (s.value / total) * 100 : 0;
        return (
          <div key={i} className="flex items-center gap-3">
            <span className="text-base w-6">{s.icon}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-sm text-[var(--text)]">{s.label}</span>
                <span className="text-xs text-[var(--muted)] ml-2">
                  {pct.toFixed(1)}%
                </span>
              </div>
              <div className="h-2 rounded-full bg-[var(--border)] overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${Math.max(pct, 1)}%`,
                    backgroundColor: s.color,
                  }}
                />
              </div>
            </div>
            <span className="text-xs text-[var(--muted)] w-24 text-right whitespace-nowrap">
              {formatBRL(s.value)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function MunicipioDetailPage() {
  const { codigoIbge } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tradeYear] = useState(2025);
  const [showAllCandidatos, setShowAllCandidatos] = useState(false);

  useEffect(() => {
    if (!codigoIbge) return;
    let alive = true;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await axios.get(`/municipio/${codigoIbge}`, {
          params: { year: tradeYear },
        });
        if (!alive) return;
        if (res.data?.success) {
          setData(res.data.data);
        } else {
          setError("Município não encontrado");
        }
      } catch (err) {
        if (alive) {
          setError(
            err.response?.status === 404
              ? "Município não encontrado"
              : "Erro ao carregar dados"
          );
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [codigoIbge, tradeYear]);

  const pib = data?.pib;
  const comercio = data?.comercio;
  const saude = data?.saude;
  const ranking = data?.ranking;

  const maxExport = useMemo(() => {
    if (!comercio?.topExportacoes?.length) return 0;
    return Math.max(...comercio.topExportacoes.map((p) => p.valor));
  }, [comercio]);

  const maxImport = useMemo(() => {
    if (!comercio?.topImportacoes?.length) return 0;
    return Math.max(...comercio.topImportacoes.map((p) => p.valor));
  }, [comercio]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto py-12 text-center">
        <div className="inline-block w-8 h-8 border-4 border-[var(--accent)] border-t-transparent rounded-full animate-spin" />
        <p className="mt-4 text-[var(--muted)]">Carregando dados do município...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-5xl mx-auto py-12 text-center">
        <p className="text-xl text-red-500 mb-4">{error || "Erro desconhecido"}</p>
        <button
          onClick={() => navigate(-1)}
          className="text-[var(--accent)] hover:underline flex items-center gap-2 mx-auto"
        >
          <ArrowLeft size={16} /> Voltar
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      <SEO
        title={`${data.nome} (${data.uf}) - Dados do Município`}
        description={`População, PIB, política e comércio exterior de ${data.nome}, ${data.uf}, com dados oficiais do IBGE e TSE.`}
        path={`/municipio/${codigoIbge}`}
      />
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-[var(--muted)] hover:text-[var(--accent)] transition-colors mb-3"
        >
          <ArrowLeft size={16} /> Voltar
        </button>

        <div className="flex items-center gap-3 flex-wrap">
          <MapPin size={28} className="text-[var(--accent)]" />
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[var(--text)]">
              {data.nome}
            </h1>
            <p className="text-sm text-[var(--muted)]">
              {data.uf} &middot; IBGE {data.codigoIbge}
            </p>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <KPICard
          label="População"
          value={data.populacao > 0 ? formatPop(data.populacao) : "—"}
          icon={<Users size={18} />}
          color="purple"
          subtitle={ranking?.populacao ? `${ranking.populacao}º Brasil · ${ranking.populacaoUf}º ${data.uf}` : "IBGE 2025"}
        />
        <KPICard
          label="PIB Total"
          value={pib ? formatBRL(pib.pibTotal) : "—"}
          icon={<BarChart3 size={18} />}
          color="emerald"
          subtitle={ranking?.pibTotal ? `${ranking.pibTotal}º Brasil · ${ranking.pibTotalUf}º ${data.uf}` : (pib ? `Ano ${pib.ano}` : "")}
        />
        <KPICard
          label="PIB per capita"
          value={
            pib && pib.pibPerCapita > 0
              ? `R$ ${(pib.pibPerCapita * 1000).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}`
              : "—"
          }
          subtitle={ranking?.pibPerCapita ? `${ranking.pibPerCapita}º Brasil · ${ranking.pibPerCapitaUf}º ${data.uf}` : (pib ? `Ano ${pib.ano}` : "")}
          icon={<DollarSign size={18} />}
          color="blue"
        />
        <KPICard
          label="Saldo Comercial"
          value={formatUSD(comercio?.saldo || 0)}
          icon={
            (comercio?.saldo || 0) >= 0 ? (
              <TrendingUp size={18} />
            ) : (
              <TrendingDown size={18} />
            )
          }
          color={(comercio?.saldo || 0) >= 0 ? "green" : "red"}
          subtitle={`Ano ${comercio?.ano || tradeYear}`}
        />
        <KPICard
          label="Estab. de Saúde"
          value={data.saude?.total > 0 ? formatPop(data.saude.total) : "—"}
          icon={<Stethoscope size={18} />}
          color="cyan"
          subtitle="CNES/DATASUS"
        />
      </div>

      {/* PIB Section */}
      {pib && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* VAB Composition */}
          <SectionCard
            title="Composição do PIB"
            subtitle={`Valor Adicionado Bruto — ${pib.ano}`}
          >
            <VABDonut vab={pib} />
            {pib.impostos > 0 && (
              <div className="mt-4 pt-4 border-t border-[var(--border)] flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-[var(--muted)]">
                  <Landmark size={14} />
                  <span>Impostos líquidos</span>
                </div>
                <span className="text-sm font-semibold text-[var(--text)]">
                  {formatBRL(pib.impostos)}
                </span>
              </div>
            )}
          </SectionCard>

          {/* PIB Highlights */}
          <SectionCard
            title="Destaques Econômicos"
            subtitle={`IBGE / SIDRA — ${pib.ano}`}
          >
            <div className="space-y-3">
              {[
                {
                  label: "PIB Total",
                  value: formatBRL(pib.pibTotal),
                  icon: <BarChart3 size={16} />,
                  color: "text-emerald-500",
                },
                {
                  label: "PIB per capita",
                  value: `R$ ${(pib.pibPerCapita * 1000).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}`,
                  icon: <DollarSign size={16} />,
                  color: "text-blue-500",
                },
                {
                  label: "Agropecuária",
                  value: formatBRL(pib.vabAgro),
                  icon: <Wheat size={16} />,
                  color: "text-green-500",
                },
                {
                  label: "Indústria",
                  value: formatBRL(pib.vabIndustria),
                  icon: <Factory size={16} />,
                  color: "text-blue-500",
                },
                {
                  label: "Serviços",
                  value: formatBRL(pib.vabServicos),
                  icon: <Building2 size={16} />,
                  color: "text-amber-500",
                },
                {
                  label: "Adm. Pública",
                  value: formatBRL(pib.vabAdmPublica),
                  icon: <Landmark size={16} />,
                  color: "text-purple-500",
                },
              ]
                .filter((item) => {
                  const num = parseFloat(String(item.value).replace(/[^\d,-]/g, "").replace(",", "."));
                  return !isNaN(num) && num > 0;
                })
                .map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-[var(--border)]/30 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className={item.color}>{item.icon}</span>
                      <span className="text-sm text-[var(--text)]">
                        {item.label}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-[var(--text)]">
                      {item.value}
                    </span>
                  </div>
                ))}
            </div>
          </SectionCard>
        </div>
      )}

      {/* Trade Section */}
      {comercio && (comercio.exportacao > 0 || comercio.importacao > 0) && (
        <>
          <div className="mb-4">
            <h2 className="text-xl font-bold text-[var(--text)]">
              Comércio Exterior — {comercio.ano}
            </h2>
            <p className="text-sm text-[var(--muted)]">
              Dados ComexStat (FOB USD)
            </p>
          </div>

          {/* Trade KPIs */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl p-4 text-center">
              <p className="text-xs text-[var(--muted)] uppercase tracking-wider mb-1">
                Exportações
              </p>
              <p className="text-lg font-bold text-green-500">
                {formatUSD(comercio.exportacao)}
              </p>
            </div>
            <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl p-4 text-center">
              <p className="text-xs text-[var(--muted)] uppercase tracking-wider mb-1">
                Importações
              </p>
              <p className="text-lg font-bold text-orange-500">
                {formatUSD(comercio.importacao)}
              </p>
            </div>
            <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl p-4 text-center">
              <p className="text-xs text-[var(--muted)] uppercase tracking-wider mb-1">
                Saldo
              </p>
              <p
                className={`text-lg font-bold ${comercio.saldo >= 0 ? "text-blue-500" : "text-red-500"}`}
              >
                {formatUSD(comercio.saldo)}
              </p>
            </div>
          </div>

          {/* Top Products */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {comercio.topExportacoes?.length > 0 && (
              <SectionCard
                title="Principais Exportações"
                subtitle={`Top ${comercio.topExportacoes.length} produtos`}
              >
                <div className="space-y-1">
                  {comercio.topExportacoes.map((p, i) => (
                    <ProductBar
                      key={p.sh4 || i}
                      rank={i + 1}
                      name={p.produto}
                      value={p.valor}
                      maxValue={maxExport}
                      color="#22c55e"
                    />
                  ))}
                </div>
              </SectionCard>
            )}

            {comercio.topImportacoes?.length > 0 && (
              <SectionCard
                title="Principais Importações"
                subtitle={`Top ${comercio.topImportacoes.length} produtos`}
              >
                <div className="space-y-1">
                  {comercio.topImportacoes.map((p, i) => (
                    <ProductBar
                      key={p.sh4 || i}
                      rank={i + 1}
                      name={p.produto}
                      value={p.valor}
                      maxValue={maxImport}
                      color="#f59e0b"
                    />
                  ))}
                </div>
              </SectionCard>
            )}
          </div>
        </>
      )}

      {/* No trade data message */}
      {(!comercio || (comercio.exportacao === 0 && comercio.importacao === 0)) && (
        <SectionCard className="mb-6 text-center">
          <Package size={32} className="mx-auto text-[var(--muted)] mb-2" />
          <p className="text-[var(--muted)]">
            Nenhum dado de comércio exterior disponível para {tradeYear}
          </p>
        </SectionCard>
      )}

      {/* Saúde Section */}
      {saude && saude.total > 0 && (
        <>
          <div className="mb-4">
            <h2 className="text-xl font-bold text-[var(--text)]">
              Saúde
            </h2>
            <p className="text-sm text-[var(--muted)]">
              {formatPop(saude.total)} estabelecimentos &middot; CNES/DATASUS
            </p>
          </div>

          {saude.porServico?.length > 0 && (
            <SectionCard title="Principais Serviços" subtitle="Por número de estabelecimentos" className="mb-6">
              <div className="space-y-1">
                {saude.porServico.map((s, i) => {
                  const maxServico = saude.porServico[0]?.total || 1;
                  const pct = (s.total / maxServico) * 100;
                  const nomeCurto = (s.servicoNome || "").toLowerCase().replace(/(^|\s)\S/g, (l) => l.toUpperCase());
                  return (
                    <div key={s.servicoCodigo || i} className="flex items-center gap-3 py-1.5">
                      <span className="text-xs font-mono text-[var(--muted)] w-6 text-right">{i + 1}.</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-sm text-[var(--text)] truncate" title={nomeCurto}>{nomeCurto}</span>
                          <span className="text-xs text-[var(--muted)] ml-2 whitespace-nowrap">{formatPop(s.total)}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-[var(--border)] overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-sky-500 to-cyan-500 transition-all duration-500"
                            style={{ width: `${Math.max(pct, 0.5)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </SectionCard>
          )}
        </>
      )}

      {/* Políticos Section */}
      {data.politicos && data.politicos.total > 0 && (
        <>
          <div className="mb-4 mt-2">
            <h2 className="text-xl font-bold text-[var(--text)]">
              Candidatos — Eleições {data.politicos.ano}
            </h2>
            <p className="text-sm text-[var(--muted)]">
              Dados TSE &middot; {data.politicos.total} candidatos
            </p>
          </div>

          {/* Eleitos em destaque */}
          {data.politicos.eleitos?.length > 0 && (() => {
            const executivo = data.politicos.eleitos.filter(c =>
              c.cargo === "PREFEITO" || c.cargo === "VICE-PREFEITO"
            );
            const vereadores = data.politicos.eleitos.filter(c =>
              c.cargo === "VEREADOR"
            );

            const renderCandCard = (c, i) => {
              const is2turno = c.situacao === "2º TURNO";
              return (
                <Link
                  to={`/politica/candidato/${c.sqCandidato}`}
                  key={`eleito-${i}`}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-shadow hover:shadow-md ${is2turno ? "bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border-amber-500/20" : "bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/20"}`}
                >
                  <CandidatoAvatar candidato={c} foto={c.foto} nome={c.nome} size={48} borderClass={`border-2 ${is2turno ? "border-amber-500/30" : "border-green-500/30"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[var(--text)] truncate">{c.nome}</p>
                    <div className="flex items-center gap-2 text-xs text-[var(--muted)]">
                      <span className="font-semibold text-[var(--accent)]">{c.partido}</span>
                      <span>&middot;</span>
                      <span>{c.cargo}</span>
                    </div>
                    {c.ocupacao && (
                      <p className="text-xs text-[var(--muted)] truncate mt-0.5">{c.ocupacao}</p>
                    )}
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded whitespace-nowrap ${is2turno ? "text-amber-600 bg-amber-500/10" : "text-green-600 bg-green-500/10"}`}>
                    {is2turno ? "2º Turno" : "Eleito"}
                  </span>
                </Link>
              );
            };

            return (
              <>
                {/* Executivo */}
                {executivo.length > 0 && (
                  <SectionCard title="Executivo" subtitle="Prefeito e Vice-Prefeito" className="mb-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {executivo.map(renderCandCard)}
                    </div>
                  </SectionCard>
                )}

                {/* Vereadores */}
                {vereadores.length > 0 && (
                  <SectionCard title="Vereadores Eleitos" subtitle={`${vereadores.length} vereadores`} className="mb-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {vereadores.map((c, i) => (
                        <Link to={`/politica/candidato/${c.sqCandidato}`} key={`ver-${i}`} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-[var(--border)]/20 transition-colors">
                          <CandidatoAvatar candidato={c} foto={c.foto} nome={c.nome} size={32} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-[var(--text)] truncate">{c.nome}</p>
                            <span className="text-xs text-[var(--accent)]">{c.partido}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </SectionCard>
                )}
              </>
            );
          })()}

          {/* Todos os candidatos */}
          <SectionCard title="Todos os Candidatos" subtitle={`${data.politicos.total} registrados`} className="mb-6">
            <div className="space-y-1">
              {(showAllCandidatos ? data.politicos.candidatos : data.politicos.candidatos?.slice(0, 15))?.map((c, i) => {
                const isEleito = c.situacao === "ELEITO";
                return (
                  <Link
                    to={`/politica/candidato/${c.sqCandidato}`}
                    key={`cand-${i}`}
                    className={`flex items-center gap-3 py-2 px-2 rounded-lg transition-colors ${isEleito ? "bg-green-500/5" : "hover:bg-[var(--border)]/20"}`}
                  >
                    <CandidatoAvatar candidato={c} foto={c.foto} nome={c.nome} size={32} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[var(--text)] truncate">{c.nome}</span>
                        {isEleito && <span className="text-[10px] font-bold text-green-600 bg-green-500/10 px-1.5 py-0.5 rounded">ELEITO</span>}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-[var(--muted)]">
                        <span className="font-semibold text-[var(--accent)]">{c.partido}</span>
                        <span>&middot;</span>
                        <span>{c.cargo}</span>
                        {c.numero && <span>&middot; Nº {c.numero}</span>}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            {data.politicos.candidatos?.length > 15 && (
              <button
                onClick={() => setShowAllCandidatos((v) => !v)}
                className="mt-3 flex items-center gap-1 text-sm text-[var(--accent)] hover:underline mx-auto"
              >
                {showAllCandidatos ? (
                  <><ChevronUp size={14} /> Mostrar menos</>
                ) : (
                  <><ChevronDown size={14} /> Ver todos os {data.politicos.total} candidatos</>
                )}
              </button>
            )}
          </SectionCard>
        </>
      )}

      {/* Back link */}
      <div className="text-center py-6">
        <Link
          to={`/estado/${data.uf}/${tradeYear}`}
          className="text-[var(--accent)] hover:underline text-sm"
        >
          Ver todos os municípios de {data.uf}
        </Link>
      </div>
    </div>
  );
}
